import React, { useEffect, useRef, useState } from "react";
import { Badge, Box, Button, Center, createStyles, Loader, Tabs, Text } from "@mantine/core";
import AnimatedWaves from "../../components/AnimatedWaves";
import { getQnrSummary, getStatusColor, retryCall } from "./Service";
import { useMedplum } from "@medplum/react";
import {
  connectQnrResSocket,
  extractMessages,
  fetchCommunicationResource,
} from "./CommunicationServices";
import { formatDateTime, formatTime } from "@medplum/core";
import aiStartsSvg from "../../assets/img/mdi_stars.svg";
import { Summary } from "../AIRecipes/SummaryAi";
import { IconRefresh } from "@tabler/icons-react";
import { COLORS } from "../AIRecipes/costansts";


interface QuestionItem {
  questionText: string;
  answerText: string;
  status?: any;
}

interface Details {
  questions: QuestionItem[];
  questionnareResponseId?: any;
  patientName?: string;
  messages?: any;
  setMessages?: any;
  handleRetry?: any;
  launchType?: 'default' | 'embed';
}

export const Questions: React.FC<Details> = ({
  questionnareResponseId,
  questions,
  patientName,
  messages,
  setMessages,
  handleRetry,
  launchType = 'default',
}) => {
  const useStyles = createStyles(() => ({
    mainBox: {
      border: "1px solid #EDEDED ",
      width: "100%",
      padding: "16px",
      borderRadius: "8px",
      gap: "12px",
      display: "flex",
      flexDirection: "column",
    },
    box1: {
      display: "flex",
      flexDirection: "column",
      alignContent: "center",
      borderRadius: "8px",
      border: "1px solid #EDEDED ",
      padding: "12px 12px 0px 12px",
    },
    title: {
      fontWeight: 600,
      fontSize: "18px",
      lineHeight: "30px",
      color: "#262930",
    },
    innnerBox: {
      display: "flex",
      flexDirection: "row",
      justifyContent: "space-between",
    },
    details: {
      display: "flex",
      flexDirection: "column",
      alignContent: "space-between",
      gap: "2px",
    },
    heading: {
      fontWeight: 500,
      fontSize: "13px",
      lineHeight: "22px",
      color: "#818999",
    },
    value: {
      fontWeight: 500,
      fontSize: "13px",
      lineHeight: "22px",
      color: "#262930",
    },
    transcriptionTabs: {
      display: "flex",
      flexDirection: "row",
      gap: "12px",
    },
    time: {
      fontWeight: 600,
      fontSize: "13px",
      lineHeight: "24px",
      color: "#818999",
    },
    text: {
      fontWeight: 400,
      fontSize: "12px",
      lineHeight: "22px",
      color: "#262930",
    },
    animation: {
      display: "flex",
      flexDirection: "row",
    },
    badge: {
      position: "relative",
      alignItems: "center",
      right: "22px",
      scale: "0.6",
      top: "8px",
    },
    retryButton: {
      width: "100px",
      height: "26px",
      top: "6px",
      marginRight: "8px",
    },
    transcriptionTabsPanel: {
      overflowY: "auto",
      maxHeight: "400px",
      scrollBehavior: "smooth",
      "&::-webkit-scrollbar": {
        width: "1px",
      },
      "&::-webkit-scrollbar-thumb": {
        backgroundColor: "#888",
        borderRadius: "10px",
      },
      "&::-webkit-scrollbar-thumb:hover": {
        backgroundColor: "#555",
      },
      "&::-webkit-scrollbar-track": {
        background: "#f1f1f1",
      },
    },
  }));

  const { classes } = useStyles();
  const medplum = useMedplum();
  const [aiSummary, setAiSummary] = useState<any>('');
  const [loading, setLoading] = useState(false);
  const [retryLoading, setRetryLoading] = useState(false);
  const messagesContainerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (questionnareResponseId && messages.length === 0) {
      getCommunication();
    }
    else if (questionnareResponseId && !['pending', 'in-progress'].includes(questions[0]?.status))
      getCommunication();

  }, [questions]);
  useEffect(() => {
    const scrollToBottom = () => {
      if (messagesContainerRef.current) {
        messagesContainerRef.current.scrollTo({
          top: messagesContainerRef.current.scrollHeight,
          behavior: "smooth",
        });
      }
    };
    scrollToBottom();
  }, [messages]);

  const handleGenSummary = async () => {
    if (!questionnareResponseId || aiSummary) return;
    setAiSummary('Generating...');
    const summ = await getQnrSummary(medplum, questionnareResponseId);
    if (summ) setAiSummary(summ);
    else setAiSummary('<span style="color:red">Error: </span> Something went wrong while generating AI summary');
  }

  const getCommunication = () => {
    setLoading(true);
    fetchCommunicationResource(medplum, questionnareResponseId).then((res) => {
      const messages = extractMessages(res, questions);
      setLoading(false);
      setMessages(messages);
    });
  };

  const onRetry = async () => {
    setRetryLoading(true);
    await handleRetry(questionnareResponseId);
    setRetryLoading(false);
  }

  return (
    <Box className={classes.mainBox}>
      <Tabs defaultValue="Summary">
        <Box className={classes.box1}>
          <Box className={classes.innnerBox}>
            <Box className={classes.title}>Questionnaire details</Box>
            {/* {questions.map((item, index) => ( */}
            <Box>
              {["incomplete", "stopped"].includes(questions[0]?.status) && launchType === 'default' && (
                <Button
                  onClick={onRetry}
                  variant={retryLoading ? "outline" : "filled"}
                  className={classes.retryButton}
                >
                  {retryLoading && (
                    <Loader style={{ marginRight: "8px" }} size="xs" />
                  )}
                  {!retryLoading && (
                    <IconRefresh size={20} style={{ marginRight: "8px" }} />
                  )}
                  Retry
                </Button>
              )}
              <Badge
                radius={"sm"}
                size="lg"
                color={getStatusColor(questions[0]?.status)}
              >
                <Box className={classes.animation}>
                  <Text>
                    {" "}
                    {questions[0]?.status == "stopped"
                      ? "Not Answered"
                      : questions[0]?.status}
                  </Text>
                  {questions[0]?.status === "in-progress" && (
                    <Box ml={4}>
                      <AnimatedWaves />
                    </Box>
                  )}
                </Box>
              </Badge>
            </Box>
            {/* ))} */}
          </Box>
          <Tabs.List maw={500} pt={20}>
            <Tabs.Tab value="Summary" className={classes.heading}>
              Summary
            </Tabs.Tab>
            <Tabs.Tab value="Transcription" className={classes.heading}>
              {questions[0]?.status === `in-progress`
                ? `Live Transcription`
                : `Transcription`}
            </Tabs.Tab>
            <Tabs.Tab
              onClick={handleGenSummary}
              value="AI Analyse"
              className={classes.heading}
            >
              AI Analyse
            </Tabs.Tab>
          </Tabs.List>
        </Box>

        <Box mt={20} className={classes.box1}>
          <Tabs.Panel value="Summary" pb={10}
            style={launchType === 'embed' ? { maxHeight: '33vh' } : {}}
            className={launchType === 'embed' ? classes.transcriptionTabsPanel : undefined}
          >
            {questions.map((item, index) => (
              <Box key={index} className={classes.details}>
                <Box className={classes.heading}>{item.questionText}</Box>
                <Box mt={1} mb={8} className={classes.value}>
                  {item.answerText}
                </Box>
              </Box>
            ))}
          </Tabs.Panel>

          <Tabs.Panel
            value="Transcription"
            pb={10}
            className={classes.transcriptionTabsPanel}
            style={launchType === 'embed' ? { maxHeight: '33vh' } : {}}
            ref={messagesContainerRef}
          >
            {loading && (
              <Center>
                <Loader />
              </Center>
            )}
            {!loading &&
              messages.map((msg: any, index: any) => {
                let senderName = msg?.sender;

                if (msg?.sender.includes("Device")) {
                  senderName = "HC Bot";
                } else if (msg?.sender.includes("Patient")) {
                  senderName = patientName;
                }

                return (
                  <Box
                    key={index}
                    className={classes.transcriptionTabs}
                  // sx={{ overflowY: "auto", height: "100px" }}
                  >
                    <Box miw={165}>
                      <Text className={classes.time}>
                        {formatDateTime(msg?.lastUpdated)} :
                      </Text>
                    </Box>
                    <Box className={classes.details} mb={8}>
                      <Box
                        className={classes.heading}
                        style={{
                          marginTop: msg.isGenerated ? "-12px" : "auto",
                        }}
                      >
                        {senderName}
                        {msg.isGenerated && (
                          <Button
                            className={classes.badge}
                            size="xs"
                            variant="outline"
                          >
                            <img
                              src={aiStartsSvg}
                              style={{ marginRight: "2px" }}
                            />
                            AI GENERATED
                          </Button>
                        )}
                        {msg.isRecalled && (
                          <Button
                            className={classes.badge}
                            color={COLORS.btnGreen}
                            size="xs"
                            variant="outline"
                          >
                            <IconRefresh style={{ marginRight: "2px" }} />
                            CALL RESUMED
                          </Button>
                        )}
                      </Box>
                      <Box className={classes.value}>{msg?.message}</Box>
                    </Box>
                  </Box>
                );
              })}
          </Tabs.Panel>

          <Tabs.Panel value="AI Analyse" pb={10}>
            <Box className={classes.details} style={{ height: launchType === 'embed' ? "250px" : "300px" }}>
              <Summary text={aiSummary} title="AI Analyse" />
            </Box>
          </Tabs.Panel>
        </Box>
      </Tabs>
    </Box>
  );
};
